import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { UrlFormatter } from 'shared';

/**
 * Native port of the v1 `Styling` factory (account-v1/src/core/api/styling.js).
 * WS5 #10 Stage B B4: replaces the `$injector.get('Styling')` grabs in
 * ft-touchpoint-container (`getStylingInjectables` → `Styling.get(url)`) and
 * ft-touchpoint-styling (the email-template pickers → getEmail/putEmail/
 * putReminderEmails/putReengagementEmails; the CSS editors → get/put).
 *
 * All methods return the response BODY (v1 `.then(r => r.data)`; `get`/`put` are bare
 * `$http` aliases whose envelope the consumers unwrapped via `response.data` — the
 * native call sites drop that `.data`). `getParams` + `touchpointUrlMap` ported
 * verbatim; URL templates built with the native `UrlFormatter.format` (byte-identical
 * to the v1 `urlFormatter`). PUTs are fire-on-call (firstValueFrom subscribes the
 * cold HttpClient observable), matching the v1 $http fire-on-call.
 */
@Injectable({ providedIn: 'root' })
export class StylingV1ApiService {
  private readonly http = inject(HttpClient);

  private readonly touchpointUrlMap: Record<string, string> = {
    finalistsEmail: 'finalist',
    reminderEmail: 'finalistreminder',
    activityEmail: 'finalistactivity',
    winnerEmail: 'winnernotification',
    homepageEmail: 'homepage',
  };

  /**
   * Verbatim v1 `getParams` — NOTE it deliberately OMITS propertyUuid, so the
   * `:propertyUuid` token in `emailStylingUrl` is left LITERAL by UrlFormatter (a
   * byte-identical port of v1 urlFormatter, which only replaces tokens present in
   * params). This is a pre-existing v1 quirk preserved for parity (it is why the
   * email-template pickers no-op on real quebec data — getEmail hits the unfilled
   * URL → empty templates); the B4b two-host save diag proves the URL byte-identical.
   */
  private getParams(model: any): any {
    return {
      layerName: model.layerName === 'photo-advocacy' ? model.layerName : model.layerName.replace('-', ''),
      emailName: this.touchpointUrlMap[model.touchpointType] || model.touchpointType,
      touchpointUUID: model.touchpointUUID,
    };
  }

  private readonly emailStylingUrl =
    '/api/:propertyUuid/properties/layers/:layerName/touchpoints/:touchpointUUID/emails/:emailName/styling';

  /** v1 `Styling.getEmail(model)` — all available XSLT templates for a touchpoint email page. */
  getEmail(model: any): Promise<any> {
    const params = this.getParams(model);
    return firstValueFrom(this.http.get(UrlFormatter.format(this.emailStylingUrl, params)));
  }

  /** v1 `Styling.putEmail(model)` — save the single XSLT template (`{templateUUID}`). */
  putEmail(model: any): Promise<any> {
    const params = this.getParams(model);
    return firstValueFrom(
      this.http.put(UrlFormatter.format(this.emailStylingUrl, params), { templateUUID: model.templateUUID }),
    );
  }

  /** v1 `Styling.putReminderEmails(model)` — `{nonViewEmailTemplateUUID, afterViewEmailTemplateUUID}`. */
  putReminderEmails(model: any): Promise<any> {
    const params = this.getParams(model);
    return firstValueFrom(
      this.http.put(UrlFormatter.format(this.emailStylingUrl, params), {
        nonViewEmailTemplateUUID: model.nonViewEmailTemplateUUID,
        afterViewEmailTemplateUUID: model.afterViewEmailTemplateUUID,
      }),
    );
  }

  /** v1 `Styling.putReengagementEmails(model)` — `{noQuoteEmailTemplateUUID, noPhotoEmailTemplateUUID}`. */
  putReengagementEmails(model: any): Promise<any> {
    const params = this.getParams(model);
    return firstValueFrom(
      this.http.put(UrlFormatter.format(this.emailStylingUrl, params), {
        noQuoteEmailTemplateUUID: model.noQuoteEmailTemplateUUID,
        noPhotoEmailTemplateUUID: model.noPhotoEmailTemplateUUID,
      }),
    );
  }

  /** v1 `Styling.get` (= $http.get) — resolves the response BODY. */
  get(url: string): Promise<any> {
    return firstValueFrom(this.http.get(url));
  }

  /** v1 `Styling.put` (= $http.put) — resolves the response BODY. */
  put(url: string, body?: any): Promise<any> {
    return firstValueFrom(this.http.put(url, body ?? null));
  }
}
